/**
 * Custom Roles (RBAC) — tenant admin UI
 *
 * Tabs:
 *   - Roles : list / create / edit / delete custom roles for this tenant
 *   - People : assign a custom role to any user in the tenant
 *
 * Backend: /api/rbac/* (see rbac_routes.py)
 */
import { useEffect, useMemo, useState } from "react";
import { api, formatApiError } from "../lib/api";
import AppShell from "../components/AppShell";
import { Button } from "../components/ui/button";
import { Card } from "../components/ui/card";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Badge } from "../components/ui/badge";
import { Textarea } from "../components/ui/textarea";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "../components/ui/select";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "../components/ui/tabs";
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from "../components/ui/table";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription,
} from "../components/ui/dialog";
import { Switch } from "../components/ui/switch";
import { Plus, PencilSimple, Trash, Users, ShieldCheck } from "@phosphor-icons/react";
import { toast } from "sonner";

export default function CustomRoles() {
  return (
    <AppShell title="Roles & Permissions">
      <div className="max-w-6xl mx-auto" data-testid="rbac-page">
        <div className="mb-6">
          <h1 className="text-2xl font-semibold text-zinc-900">Roles &amp; Permissions</h1>
          <p className="text-sm text-zinc-600 mt-1">
            Mint custom roles that combine a base role with finer-grained permissions.
            Custom roles are layered on top of the user&apos;s existing base role.
          </p>
        </div>
        <Tabs defaultValue="roles" className="w-full">
          <TabsList>
            <TabsTrigger value="roles" data-testid="tab-roles">Roles</TabsTrigger>
            <TabsTrigger value="people" data-testid="tab-people">People</TabsTrigger>
          </TabsList>
          <TabsContent value="roles" className="mt-6"><RolesTab /></TabsContent>
          <TabsContent value="people" className="mt-6"><PeopleTab /></TabsContent>
        </Tabs>
      </div>
    </AppShell>
  );
}

/* ──────────────────────────────────────────────────────────────────────── */
function RolesTab() {
  const [roles, setRoles] = useState([]);
  const [cat, setCat] = useState({ groups: {}, base_roles: [] });
  const [editing, setEditing] = useState(null); // null | {} (new) | role object
  const [loading, setLoading] = useState(true);

  const load = async () => {
    try {
      const [r, c] = await Promise.all([
        api.get("/rbac/roles"),
        api.get("/rbac/permissions"),
      ]);
      setRoles(r.data.items || []);
      setCat(c.data);
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setLoading(false); }
  };
  useEffect(() => { load(); }, []);

  const del = async (rid) => {
    if (!window.confirm("Delete this role? Users assigned to it must be unassigned first.")) return;
    try {
      await api.delete(`/rbac/roles/${rid}`);
      toast.success("Role deleted");
      load();
    } catch (e) { toast.error(formatApiError(e)); }
  };

  if (loading) return <Card className="p-6 text-sm text-zinc-500">Loading…</Card>;

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <div className="text-sm text-zinc-500">{roles.length} custom role{roles.length === 1 ? "" : "s"}</div>
        <Button onClick={() => setEditing({})} data-testid="new-role-btn">
          <Plus size={16} weight="bold" className="mr-1" /> New role
        </Button>
      </div>
      <Card className="p-0">
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>Name</TableHead>
              <TableHead>Inherits from</TableHead>
              <TableHead>Grants</TableHead>
              <TableHead>Denies</TableHead>
              <TableHead>Status</TableHead>
              <TableHead className="text-right">Actions</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {roles.length === 0 ? (
              <TableRow><TableCell colSpan={6} className="text-center text-sm text-zinc-500 italic py-6">No custom roles yet. Click &quot;New role&quot; to create one.</TableCell></TableRow>
            ) : roles.map((r) => (
              <TableRow key={r.id} data-testid={`role-row-${r.id}`}>
                <TableCell>
                  <div className="font-medium">{r.name}</div>
                  {r.description && <div className="text-[11px] text-zinc-500 line-clamp-1">{r.description}</div>}
                </TableCell>
                <TableCell><Badge variant="outline">{r.inherits_from}</Badge></TableCell>
                <TableCell className="text-xs text-emerald-700">{(r.grants || []).join(", ") || "—"}</TableCell>
                <TableCell className="text-xs text-red-700">{(r.denies || []).join(", ") || "—"}</TableCell>
                <TableCell>
                  <Badge variant={r.is_active ? "default" : "secondary"}>{r.is_active ? "active" : "inactive"}</Badge>
                </TableCell>
                <TableCell className="text-right">
                  <Button size="sm" variant="ghost" onClick={() => setEditing(r)} data-testid={`edit-${r.id}`}>
                    <PencilSimple size={16} />
                  </Button>
                  <Button size="sm" variant="ghost" onClick={() => del(r.id)} data-testid={`delete-${r.id}`}>
                    <Trash size={16} className="text-red-600" />
                  </Button>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </Card>

      {editing !== null && (
        <RoleEditor
          role={editing}
          catalog={cat}
          onClose={() => setEditing(null)}
          onSaved={() => { setEditing(null); load(); }}
        />
      )}
    </div>
  );
}

/* ──────────────────────────────────────────────────────────────────────── */
function RoleEditor({ role, catalog, onClose, onSaved }) {
  const isNew = !role.id;
  const [form, setForm] = useState({
    name: role.name || "",
    description: role.description || "",
    inherits_from: role.inherits_from || "employee",
    grants: new Set(role.grants || []),
    denies: new Set(role.denies || []),
    is_active: role.is_active !== false,
  });
  const [saving, setSaving] = useState(false);

  const toggle = (set, key) => {
    const fresh = new Set(set);
    if (fresh.has(key)) fresh.delete(key); else fresh.add(key);
    return fresh;
  };

  const save = async () => {
    if (!form.name.trim() || form.name.length < 2) return toast.error("Name is required");
    setSaving(true);
    try {
      const body = {
        name: form.name.trim(),
        description: form.description.trim() || null,
        inherits_from: form.inherits_from,
        grants: Array.from(form.grants),
        denies: Array.from(form.denies),
      };
      if (isNew) await api.post("/rbac/roles", body);
      else await api.patch(`/rbac/roles/${role.id}`, { ...body, is_active: form.is_active });
      toast.success(isNew ? "Role created" : "Role updated");
      onSaved();
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setSaving(false); }
  };

  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-3xl">
        <DialogHeader>
          <DialogTitle>{isNew ? "New custom role" : `Edit — ${role.name}`}</DialogTitle>
          <DialogDescription>
            Configure a role that combines a base role with finer-grained permission overrides.
          </DialogDescription>
        </DialogHeader>

        <div className="grid grid-cols-2 gap-4">
          <div>
            <Label className="text-xs">Name</Label>
            <Input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} data-testid="role-name" />
          </div>
          <div>
            <Label className="text-xs">Inherits from (base role)</Label>
            <Select value={form.inherits_from} onValueChange={(v) => setForm({ ...form, inherits_from: v })}>
              <SelectTrigger data-testid="role-inherits"><SelectValue /></SelectTrigger>
              <SelectContent>
                {(catalog.base_roles || []).map((b) => (
                  <SelectItem key={b} value={b}>{b}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <div className="col-span-2">
            <Label className="text-xs">Description</Label>
            <Textarea rows={2} value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} data-testid="role-desc" />
          </div>
        </div>

        <div className="mt-2 text-xs text-zinc-500">
          Tick a permission to <span className="text-emerald-700 font-medium">grant</span> it on top of the base
          role. Click it again to <span className="text-red-700 font-medium">deny</span> it (overrides base).
          A third click clears the override.
        </div>

        <div className="max-h-[400px] overflow-y-auto border border-zinc-200 rounded">
          {Object.entries(catalog.groups || {}).map(([groupName, perms]) => (
            <div key={groupName} className="border-b border-zinc-100 last:border-b-0">
              <div className="bg-zinc-50 px-3 py-1.5 text-xs font-semibold text-zinc-600">{groupName}</div>
              <div className="px-3 py-2 space-y-1">
                {perms.map((p) => {
                  const isGrant = form.grants.has(p.key);
                  const isDeny = form.denies.has(p.key);
                  const cycle = () => {
                    let g = new Set(form.grants), d = new Set(form.denies);
                    if (!isGrant && !isDeny) { g.add(p.key); }
                    else if (isGrant) { g.delete(p.key); d.add(p.key); }
                    else { d.delete(p.key); }
                    setForm({ ...form, grants: g, denies: d });
                  };
                  return (
                    <button
                      key={p.key}
                      onClick={cycle}
                      className={`w-full flex items-center justify-between text-left px-2 py-1.5 rounded text-xs transition ${
                        isGrant ? "bg-emerald-50 text-emerald-900 border border-emerald-200"
                        : isDeny ? "bg-red-50 text-red-900 border border-red-200"
                        : "hover:bg-zinc-50 border border-transparent"
                      }`}
                      data-testid={`perm-${p.key}`}
                    >
                      <span><span className="font-mono text-[10px] text-zinc-500 mr-2">{p.key}</span>{p.label}</span>
                      <span>
                        {isGrant && <Badge className="bg-emerald-600 hover:bg-emerald-600">grant</Badge>}
                        {isDeny && <Badge variant="destructive">deny</Badge>}
                      </span>
                    </button>
                  );
                })}
              </div>
            </div>
          ))}
        </div>

        {!isNew && (
          <div className="flex items-center justify-between mt-3 text-sm">
            <Label>Role is active</Label>
            <Switch checked={form.is_active} onCheckedChange={(v) => setForm({ ...form, is_active: v })} data-testid="role-active-toggle" />
          </div>
        )}

        <DialogFooter>
          <Button variant="outline" onClick={onClose}>Cancel</Button>
          <Button onClick={save} disabled={saving} data-testid="save-role-btn">{saving ? "Saving…" : (isNew ? "Create role" : "Save changes")}</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

/* ──────────────────────────────────────────────────────────────────────── */
function PeopleTab() {
  const [users, setUsers] = useState([]);
  const [roles, setRoles] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState("");

  const load = async () => {
    try {
      const [u, r] = await Promise.all([api.get("/rbac/users"), api.get("/rbac/roles")]);
      setUsers(u.data.items || []);
      setRoles((r.data.items || []).filter((x) => x.is_active));
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setLoading(false); }
  };
  useEffect(() => { load(); }, []);

  const assign = async (uid, role_id) => {
    try {
      if (!role_id || role_id === "__none__") {
        await api.post(`/rbac/users/${uid}/unassign`);
      } else {
        await api.post(`/rbac/users/${uid}/assign`, { role_id });
      }
      toast.success("Updated");
      load();
    } catch (e) { toast.error(formatApiError(e)); }
  };

  const filtered = useMemo(() => {
    const q = filter.trim().toLowerCase();
    if (!q) return users;
    return users.filter((u) => (u.name || "").toLowerCase().includes(q) || (u.email || "").toLowerCase().includes(q));
  }, [users, filter]);

  if (loading) return <Card className="p-6 text-sm text-zinc-500">Loading…</Card>;

  return (
    <div className="space-y-4">
      <Input placeholder="Search by name or email…" value={filter} onChange={(e) => setFilter(e.target.value)} className="max-w-sm" data-testid="people-search" />
      <Card className="p-0">
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>User</TableHead>
              <TableHead>Base role</TableHead>
              <TableHead>Custom role</TableHead>
              <TableHead className="text-right">Assign</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {filtered.map((u) => (
              <TableRow key={u.id} data-testid={`user-row-${u.id}`}>
                <TableCell>
                  <div className="font-medium">{u.name || u.email}</div>
                  <div className="text-xs text-zinc-500">{u.email}</div>
                </TableCell>
                <TableCell><Badge variant="outline">{u.role}</Badge></TableCell>
                <TableCell>{u.custom_role_name ? <Badge>{u.custom_role_name}</Badge> : <span className="text-xs text-zinc-400 italic">none</span>}</TableCell>
                <TableCell className="text-right">
                  <Select value={u.custom_role_id || "__none__"} onValueChange={(v) => assign(u.id, v)}>
                    <SelectTrigger className="w-[180px] ml-auto" data-testid={`assign-${u.id}`}><SelectValue placeholder="Pick role" /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="__none__">— None —</SelectItem>
                      {roles.map((r) => <SelectItem key={r.id} value={r.id}>{r.name}</SelectItem>)}
                    </SelectContent>
                  </Select>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </Card>
    </div>
  );
}
